import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { JobPromotion } from '../../core/job/job.model';
import { UniversalModalComponent } from '../../ui/universal-modal/universal-modal.component';
import { IAppState } from '../../ngrx';
import * as ModalActions from '../../ngrx/modal/actions/modal.action';
import moment from 'moment';


@Injectable({ providedIn: 'root' })
export class PromotionService {
  router = inject(Router);
  store = inject(Store<IAppState>);
  translate = inject(TranslateService);

  suggestActivatePromotion(promotions: JobPromotion[], data: {
    searchName: string,
    jobId: number
  }): void {
    const { searchName, jobId } = data;
    const route = `/${searchName}/endre-stilling/${jobId}/promotion`;
    const modalName = 'suggest-activate-promotion-modal';

    let description = '';
    if (promotions?.length > 1) {
      description = 'PROMOTION.ACTIVATE.BOTH';
    } else {
      const name = promotions?.[0].name;
      description = this.translate.instant('PROMOTION.ACTIVATE.ONE', { promotion: name });
    }

    this.store.dispatch(
      ModalActions.open({
        cmpType: UniversalModalComponent,
        fixed: true,
        props: {
          id: modalName,
          modalName,
          title: 'PROMOTION.PUBLISH.TITLE',
          description,
          buttons: [
            {
              id: 'no',
              caption: 'COMMON.NO',
              view: 'outlined-cancel'
            },
            {
              id: 'yes',
              caption: 'COMMON.YES',
              view: 'primary',
              handler: () => {
                this.router.navigate([route]);
              }
            }
          ]
        },
      })
    );
  }

  /**
   * Calculates promotion end date, capped by job deadline if provided.
   * This shared logic is used by both Finn and Jobtip services to ensure consistent
   * behavior across all promotion types.
   *
   * @param maxPossibleEndDate - Maximum allowed end date for this promotion type
   * @param jobDeadline - Optional job deadline that may cap the promotion duration
   * @returns The calculated end date (earlier of maxPossible or jobDeadline)
   */
  calculatePromotionEndDate(
    maxPossibleEndDate: moment.Moment,
    jobDeadline?: moment.Moment
  ): moment.Moment {
    if (!jobDeadline) {
      return maxPossibleEndDate;
    }

    return jobDeadline.isBefore(maxPossibleEndDate)
      ? jobDeadline
      : maxPossibleEndDate;
  }

  /**
   * Calculates maximum possible promotion end date based on start date and duration.
   *
   * @param start - The start date for the promotion campaign
   * @param durationDays - Duration in days for this promotion type
   * @returns The maximum end date (start date + duration)
   */
  calculateMaxPossibleEndDate(
    start: moment.Moment,
    durationDays: number
  ): moment.Moment {
    return moment(start).add(durationDays, 'days');
  }
}
